import { createContext, useContext, useMemo, useState, useCallback, useRef } from "react";
import type { CreateLocalActionRequest } from "~/pages/administration/workflows-page/workflows-dialog/views/action-view/hooks/use-action-view";
import type {
  Workflow,
  CreateFieldRequest,
  CreateStatusRequest,
  CreateWorkflowRequest,
} from "~/types/api";

const views = {
  default: "default",
  fields: "fields",
  statuses: "statuses",
  actions: "actions",
} as const;
export type View = (typeof views)[keyof typeof views];

type CreateWorkflowState = {
  workflow: Partial<CreateWorkflowRequest> | null;
  fields: CreateFieldRequest[];
  statuses: CreateStatusRequest[];
  committedStatuses: CreateStatusRequest[];
  actions: CreateLocalActionRequest[];
  committedActions: CreateLocalActionRequest[];
  activeActionId: number | null;
  currentView: View;
  isUpgradeMode: boolean;
  sourceWorkflowId: number | null;
};

export type OnAfterCreateFn = (createdWorkflowId: number) => Promise<void>;

export type UpdateWorkflowState = <K extends keyof CreateWorkflowState>(
  key: K,
  value: CreateWorkflowState[K]
) => void;

type WorkflowCreationContextType = {
  state: CreateWorkflowState;
  updateState: UpdateWorkflowState;
  resetState: () => void;
  initializeFromWorkflow: (workflow: Workflow, isUpgrade?: boolean) => void;
  onAfterCreate: React.RefObject<OnAfterCreateFn | null>;
};

const WorkflowCreationContext = createContext<WorkflowCreationContextType | undefined>(undefined);

const initialState: CreateWorkflowState = {
  workflow: null,
  fields: [],
  statuses: [],
  committedStatuses: [],
  actions: [],
  committedActions: [],
  activeActionId: null,
  currentView: views.default,
  isUpgradeMode: false,
  sourceWorkflowId: null,
};

export const WorkflowCreationContextProvider = ({ children }: { children: React.ReactNode }) => {
  const [state, setState] = useState<CreateWorkflowState>(initialState);
  const onAfterCreateRef = useRef<OnAfterCreateFn | null>(null);

  const updateState = useCallback(
    <K extends keyof CreateWorkflowState>(key: K, value: CreateWorkflowState[K]) => {
      setState((prev) => ({
        ...prev,
        [key]: value,
      }));
    },
    []
  );

  const resetState = useCallback(() => {
    setState(initialState);
    onAfterCreateRef.current = null;
  }, []);

  const initializeFromWorkflow = useCallback((workflow: Workflow, isUpgrade = false) => {
    const fields: CreateFieldRequest[] = (workflow.fields || []).map((f) => ({
      id: f.id,
      name: f.name,
      type: f.type,
      order: f.order,
      required: f.required,
      options: f.options ?? null,
    }));

    const statuses: CreateStatusRequest[] = (workflow.statuses || []).map((s) => ({
      code: s.code,
      name: s.name,
    }));

    //TODO: sortOrder LOGIC change...
    const actions: CreateLocalActionRequest[] = (workflow.procedures ?? [])
      .map((proc) => ({
        id: proc.id,
        name: proc.name,
        configuration: proc.configuration,
        statusCodes: (proc.statuses ?? []).map((s) => s.statusCode),
        sortOrder: proc.statuses?.[0]?.sortOrder ?? 0,
      }))
      .sort((a, b) => a.sortOrder - b.sortOrder);

    setState({
      workflow: {
        customerId: workflow.customerId,
        name: isUpgrade ? workflow.name : "",
        previousVersionId: isUpgrade ? workflow.id : null,
      },
      fields,
      statuses,
      committedStatuses: statuses,
      actions,
      committedActions: actions,
      activeActionId: null,
      currentView: views.default,
      isUpgradeMode: isUpgrade,
      sourceWorkflowId: workflow.id,
    });
  }, []);

  const value = useMemo(
    () => ({
      state,
      updateState,
      resetState,
      initializeFromWorkflow,
      onAfterCreate: onAfterCreateRef,
    }),
    [state, updateState, resetState, initializeFromWorkflow]
  );

  return (
    <WorkflowCreationContext.Provider value={value}>{children}</WorkflowCreationContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useWorkflowCreationContext = () => {
  const context = useContext(WorkflowCreationContext);
  if (!context) {
    throw new Error(
      "useWorkflowCreationContext must be used within a WorkflowCreationContextProvider"
    );
  }
  return context;
};
